import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  makeStyles,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  CircularProgress
} from '@material-ui/core';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  defaults
} from 'chart.js';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import moment from 'moment';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DevicesIcon from '@material-ui/icons/Devices';
import ClearIcon from '@material-ui/icons/Clear';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { _convertObjectToQuery } from 'src/app/utils/apiService';

// Configure modern typography and sleek defaults for all Chart.js charts
defaults.font.family =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
defaults.font.size = 12;
defaults.font.weight = '500';
defaults.color = '#475569';

// Register Chart.js modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3)
  },
  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
  },
  kpiGridItem: {
    display: 'flex'
  },
  kpiCard: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: 110,
    boxSizing: 'border-box',
    transition: 'transform 0.2s ease, boxShadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 18px rgba(0,0,0,0.09)'
    }
  },
  iconBox: {
    width: 52,
    height: 52,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    flexShrink: 0,
    marginLeft: theme.spacing(1.5)
  },
  chartCard: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box'
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: 12
  },
  chartTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: '#1a1f36',
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  legendList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 12px',
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    fontSize: 13
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 8
  }
}));

const formatMoney = amount =>
  new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(amount || 0);

const SalesDashboard = ({
  listOrders = [],
  listDeviceType = [],
  listSales = [],
  isAdmin,
  isAccountant,
  isLeadMarketing
}) => {
  const classes = useStyles();

  // Local state for Dashboard filters (isolated from Orders Table)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterSale, setFilterSale] = useState('all');

  // Multi-page fetched orders state to support >100 total orders
  const [allOrders, setAllOrders] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [totalServerOrders, setTotalServerOrders] = useState(0);

  // Receipts state from /receipt
  const [allReceipts, setAllReceipts] = useState([]);
  const [receiptTotalAmountSum, setReceiptTotalAmountSum] = useState(0);
  const [totalServerReceipts, setTotalServerReceipts] = useState(0);

  // Chart 1 Metric View: 'revenue' (Doanh thu) | 'volume' (Số lượng đơn & thiết bị)
  const [metricView, setMetricView] = useState('revenue');
  // Grouping state for revenue chart: 'auto' | 'day' | 'week' | 'month'
  const [groupBy, setGroupBy] = useState('auto');

  // Compute time preset based on current fromDate and toDate
  const timePreset = useMemo(() => {
    if (!fromDate && !toDate) return 'all';
    const today = moment().format('YYYY-MM-DD');
    if (fromDate === today && toDate === today) return 'today';
    const startWeek = moment().startOf('isoweek').format('YYYY-MM-DD');
    const endWeek = moment().endOf('isoweek').format('YYYY-MM-DD');
    if (fromDate === startWeek && toDate === endWeek) return 'this_week';
    const startMonth = moment().startOf('month').format('YYYY-MM-DD');
    const endMonth = moment().endOf('month').format('YYYY-MM-DD');
    if (fromDate === startMonth && toDate === endMonth) return 'this_month';
    return 'custom';
  }, [fromDate, toDate]);

  // Fetch all pages of orders and receipts in parallel
  useEffect(() => {
    let isMounted = true;
    const fetchAllPages = async () => {
      setLoadingAll(true);
      try {
        const pageSize = 1000;
        const queryObj = {
          page: 1,
          page_size: pageSize
        };
        if (fromDate) queryObj.from_date = fromDate;
        if (toDate) queryObj.to_date = toDate;
        if (filterSale && filterSale !== 'all') queryObj.sale_id = filterSale;

        const receiptQueryObj = {
          page: 1,
          page_size: pageSize
        };
        if (fromDate) receiptQueryObj.from_date = fromDate;
        if (toDate) receiptQueryObj.to_date = toDate;

        const [orderRes, receiptRes] = await Promise.all([
          axiosInterceptor.get(`/order?${_convertObjectToQuery(queryObj)}`),
          axiosInterceptor.get(`/receipt?${_convertObjectToQuery(receiptQueryObj)}`)
        ]);

        // Process Orders
        const firstPayload = orderRes.data?.payload || {};
        const firstOrders = firstPayload.orders || orderRes.data?.orders || (Array.isArray(firstPayload) ? firstPayload : []);
        const total = orderRes.data?.numberOfItem || orderRes.data?.total || firstPayload.total || firstOrders.length;
        let combinedOrders = [...firstOrders];

        if (total > pageSize) {
          const totalPages = Math.ceil(total / pageSize);
          const promises = [];
          for (let p = 2; p <= Math.min(totalPages, 50); p++) {
            const pageQuery = { ...queryObj, page: p, page_size: pageSize };
            promises.push(
              axiosInterceptor.get(`/order?${_convertObjectToQuery(pageQuery)}`)
            );
          }
          const extraResponses = await Promise.all(promises);
          extraResponses.forEach(r => {
            const pData = r.data?.payload || {};
            const orders = pData.orders || r.data?.orders || (Array.isArray(pData) ? pData : []);
            combinedOrders = combinedOrders.concat(orders);
          });
        }

        // Process Receipts
        const rPayload = receiptRes.data?.payload || {};
        const firstReceipts = rPayload.receipts || rPayload.data || (Array.isArray(rPayload) ? rPayload : []);
        const totalRecs = receiptRes.data?.numberOfItem ?? receiptRes.data?.total ?? firstReceipts.length;
        const serverRecAmountSum = receiptRes.data?.total_amount_sum;
        let combinedReceipts = [...firstReceipts];

        if (totalRecs > pageSize) {
          const totalRPages = Math.ceil(totalRecs / pageSize);
          const rPromises = [];
          for (let p = 2; p <= Math.min(totalRPages, 50); p++) {
            rPromises.push(
              axiosInterceptor.get(`/receipt?${_convertObjectToQuery({ ...receiptQueryObj, page: p, page_size: pageSize })}`)
            );
          }
          const extraRResponses = await Promise.all(rPromises);
          extraRResponses.forEach(r => {
            const pData = r.data?.payload || {};
            const recs = pData.receipts || pData.data || (Array.isArray(pData) ? pData : []);
            combinedReceipts = combinedReceipts.concat(recs);
          });
        }

        const calculatedRecSum = serverRecAmountSum !== undefined && serverRecAmountSum !== null
          ? Number(serverRecAmountSum)
          : combinedReceipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

        if (isMounted) {
          setAllOrders(combinedOrders);
          setTotalServerOrders(total);
          setAllReceipts(combinedReceipts);
          setTotalServerReceipts(totalRecs);
          setReceiptTotalAmountSum(calculatedRecSum);
        }
      } catch (err) {
        console.error('Fetch all orders/receipts for dashboard failed:', err);
        if (isMounted) {
          setAllOrders([]);
          setTotalServerOrders(0);
          setAllReceipts([]);
          setTotalServerReceipts(0);
          setReceiptTotalAmountSum(0);
        }
      } finally {
        if (isMounted) {
          setLoadingAll(false);
        }
      }
    };

    fetchAllPages();

    return () => {
      isMounted = false;
    };
  }, [fromDate, toDate, filterSale]);

  const handleTimePresetChange = e => {
    const val = e.target.value;
    if (val === 'all') {
      setFromDate('');
      setToDate('');
    } else if (val === 'today') {
      const today = moment().format('YYYY-MM-DD');
      setFromDate(today);
      setToDate(today);
    } else if (val === 'this_week') {
      setFromDate(moment().startOf('isoweek').format('YYYY-MM-DD'));
      setToDate(moment().endOf('isoweek').format('YYYY-MM-DD'));
    } else if (val === 'this_month') {
      setFromDate(moment().startOf('month').format('YYYY-MM-DD'));
      setToDate(moment().endOf('month').format('YYYY-MM-DD'));
    }
  };

  const filteredOrders = allOrders;

  // Compute effective grouping based on selected option or automatic timeframe span
  const effectiveGroupBy = useMemo(() => {
    if (groupBy !== 'auto') return groupBy;

    const allDates = [
      ...filteredOrders.map(o => new Date(o.created_at || o.createdAt).getTime()),
      ...allReceipts.map(r => new Date(r.receipt_date || r.date || r.created_at).getTime())
    ].filter(t => !isNaN(t));

    if (!allDates.length) return 'day';

    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);
    const diffDays = Math.ceil((maxDate - minDate) / (1000 * 3600 * 24));

    if (diffDays > 45) return 'month';
    if (diffDays > 14) return 'week';
    return 'day';
  }, [groupBy, filteredOrders, allReceipts]);

  // KPI Calculations
  const kpiData = useMemo(() => {
    let totalRevenue = 0; // Tổng tiền các đơn
    let deliveredRevenue = 0; // Tiền các đơn đã giao / hoàn thành
    let totalDevicesCount = 0;
    let completedCount = 0;
    let pendingCount = 0;
    let cancelledCount = 0;

    filteredOrders.forEach(order => {
      const amount = order.total_amount || order.totalAmount || 0;

      if (order.status !== -1) {
        totalRevenue += amount;

        const devices = order.order_device_types || order.devices || [];
        devices.forEach(d => {
          totalDevicesCount += Number(d.quantity) || 0;
        });
      }

      if (order.status === 3) {
        deliveredRevenue += amount;
        completedCount++;
      } else if (order.status === -1) {
        cancelledCount++;
      } else {
        pendingCount++;
      }
    });

    const totalOrders = filteredOrders.length;
    const completionRate = totalOrders > 0 ? Math.round((completedCount / totalOrders) * 100) : 0;
    const actualReceived = receiptTotalAmountSum;
    const debtAmount = Math.max(0, totalRevenue - actualReceived);

    return {
      totalRevenue,
      deliveredRevenue,
      actualReceived,
      debtAmount,
      totalReceipts: totalServerReceipts || allReceipts.length,
      totalOrders,
      completedCount,
      pendingCount,
      cancelledCount,
      totalDevicesCount,
      completionRate
    };
  }, [filteredOrders, receiptTotalAmountSum, totalServerReceipts, allReceipts]);

  // Chart 1: Clean 2-Bar Charts (Revenue View vs Volume View)
  const revenueChartData = useMemo(() => {
    const dateMap = {};

    // 1. Process orders
    filteredOrders.forEach(order => {
      const mDate = moment(order.created_at || order.createdAt);
      if (!mDate.isValid()) return;

      let key = mDate.format('DD/MM');
      let sortKey = mDate.format('YYYYMMDD');
      if (effectiveGroupBy === 'month') {
        key = `Tháng ${mDate.format('MM/YYYY')}`;
        sortKey = mDate.format('YYYYMM');
      } else if (effectiveGroupBy === 'week') {
        key = `Tuần ${mDate.isoWeek()}`;
        sortKey = `${mDate.isoWeekYear()}${String(mDate.isoWeek()).padStart(2, '0')}`;
      }

      if (!dateMap[key]) {
        dateMap[key] = { sortKey, totalRevenue: 0, receiptRevenue: 0, count: 0, deviceCount: 0 };
      }
      const amount = (order.total_amount || order.totalAmount || 0) / 1000000; // Millions VND
      if (order.status !== -1) {
        dateMap[key].totalRevenue += amount;

        const devices = order.order_device_types || order.devices || [];
        devices.forEach(d => {
          dateMap[key].deviceCount += Number(d.quantity) || 0;
        });
      }
      dateMap[key].count += 1;
    });

    // 2. Process receipts
    allReceipts.forEach(receipt => {
      const rDate = moment(receipt.receipt_date || receipt.date || receipt.created_at);
      if (!rDate.isValid()) return;

      let key = rDate.format('DD/MM');
      let sortKey = rDate.format('YYYYMMDD');
      if (effectiveGroupBy === 'month') {
        key = `Tháng ${rDate.format('MM/YYYY')}`;
        sortKey = rDate.format('YYYYMM');
      } else if (effectiveGroupBy === 'week') {
        key = `Tuần ${rDate.isoWeek()}`;
        sortKey = `${rDate.isoWeekYear()}${String(rDate.isoWeek()).padStart(2, '0')}`;
      }

      if (!dateMap[key]) {
        dateMap[key] = { sortKey, totalRevenue: 0, receiptRevenue: 0, count: 0, deviceCount: 0 };
      }
      const rAmount = (Number(receipt.amount) || 0) / 1000000;
      dateMap[key].receiptRevenue += rAmount;
    });

    // Sort labels chronologically
    const sortedEntries = Object.entries(dateMap).sort((a, b) => (a[1].sortKey || '').localeCompare(b[1].sortKey || ''));
    const labels = sortedEntries.map(e => e[0]);
    const totalRevenues = sortedEntries.map(e => Math.round(e[1].totalRevenue * 10) / 10);
    const receiptRevenues = sortedEntries.map(e => Math.round(e[1].receiptRevenue * 10) / 10);
    const counts = sortedEntries.map(e => e[1].count);
    const deviceCounts = sortedEntries.map(e => e[1].deviceCount);

    if (metricView === 'volume') {
      return {
        labels: labels.length > 0 ? labels : ['Chưa có dữ liệu'],
        datasets: [
          {
            type: 'bar',
            label: 'Số lượng đơn hàng',
            data: counts.length > 0 ? counts : [0],
            backgroundColor: '#f59e0b',
            borderRadius: 6,
            maxBarThickness: 38,
            yAxisID: 'y'
          },
          {
            type: 'bar',
            label: 'Số lượng thiết bị bán ra',
            data: deviceCounts.length > 0 ? deviceCounts : [0],
            backgroundColor: '#8b5cf6',
            borderRadius: 6,
            maxBarThickness: 38,
            yAxisID: 'y'
          }
        ]
      };
    }

    // Default 'revenue' view
    return {
      labels: labels.length > 0 ? labels : ['Chưa có dữ liệu'],
      datasets: [
        {
          type: 'bar',
          label: 'Tổng tiền các đơn (Triệu VNĐ)',
          data: totalRevenues.length > 0 ? totalRevenues : [0],
          backgroundColor: '#3b82f6',
          borderRadius: 6,
          maxBarThickness: 38,
          yAxisID: 'y'
        },
        {
          type: 'bar',
          label: 'Tiền đã thu (Phiếu thu) (Triệu VNĐ)',
          data: receiptRevenues.length > 0 ? receiptRevenues : [0],
          backgroundColor: '#10b981',
          borderRadius: 6,
          maxBarThickness: 38,
          yAxisID: 'y'
        }
      ]
    };
  }, [filteredOrders, allReceipts, effectiveGroupBy, metricView]);

  // Chart 2: Top Device Types
  const topDevicesChartData = useMemo(() => {
    const deviceMap = {};
    filteredOrders.forEach(order => {
      if (order.status === -1) return;
      const devices = order.order_device_types || order.devices || [];
      devices.forEach(d => {
        const typeName =
          d.device_type?.name ||
          d.device_type_name ||
          d.type ||
          listDeviceType.find(lt => lt.id === d.device_type_id)?.name ||
          'Khác';
        deviceMap[typeName] = (deviceMap[typeName] || 0) + (Number(d.quantity) || 0);
      });
    });

    const labels = Object.keys(deviceMap);
    const quantities = Object.values(deviceMap);

    return {
      labels: labels.length > 0 ? labels : ['Chưa có dữ liệu'],
      datasets: [
        {
          label: 'Số lượng bán',
          data: quantities.length > 0 ? quantities : [0],
          backgroundColor: [
            '#3b82f6',
            '#10b981',
            '#f59e0b',
            '#8b5cf6',
            '#ec4899',
            '#06b6d4'
          ],
          borderRadius: 6,
          maxBarThickness: 32
        }
      ]
    };
  }, [filteredOrders, listDeviceType]);

  // Chart 3: Payment Method Distribution
  const paymentMethodDetails = useMemo(() => {
    const paymentMap = { 'Tiền mặt': 0, 'Chuyển khoản': 0, 'Công nợ': 0, 'COD': 0 };
    let total = 0;
    filteredOrders.forEach(order => {
      const method = order.payment_method || order.paymentMethod || 'Tiền mặt';
      if (paymentMap[method] !== undefined) {
        paymentMap[method] += 1;
        total += 1;
      }
    });

    const colors = {
      'Tiền mặt': '#10b981',
      'Chuyển khoản': '#3b82f6',
      'Công nợ': '#f59e0b',
      'COD': '#8b5cf6'
    };

    const items = Object.keys(paymentMap).map(key => ({
      label: key,
      count: paymentMap[key],
      percent: total > 0 ? Math.round((paymentMap[key] / total) * 100) : 0,
      color: colors[key]
    }));

    const chartData = {
      labels: items.map(i => i.label),
      datasets: [
        {
          data: items.map(i => i.count),
          backgroundColor: items.map(i => i.color),
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverOffset: 4
        }
      ]
    };

    return { items, chartData, total };
  }, [filteredOrders]);

  // Chart 4: Customer Type Breakdown (Đại lý vs Khách lẻ)
  const customerTypeDetails = useMemo(() => {
    let agencyCount = 0;
    let endUserCount = 0;

    filteredOrders.forEach(order => {
      if (order.customer_type === 'AGENCY') agencyCount++;
      else endUserCount++;
    });

    const total = filteredOrders.length;
    const items = [
      {
        label: 'Đại lý',
        count: agencyCount,
        percent: total > 0 ? Math.round((agencyCount / total) * 100) : 0,
        color: '#3b82f6'
      },
      {
        label: 'Khách lẻ',
        count: endUserCount,
        percent: total > 0 ? Math.round((endUserCount / total) * 100) : 0,
        color: '#ec4899'
      }
    ];

    const chartData = {
      labels: items.map(i => i.label),
      datasets: [
        {
          data: items.map(i => i.count),
          backgroundColor: items.map(i => i.color),
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverOffset: 4
        }
      ]
    };

    return { items, chartData, total };
  }, [filteredOrders]);

  return (
    <Box className={classes.root}>
      {/* Filters Toolbar */}
      <Card className={classes.filterCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px' }}>
                Thời gian
              </InputLabel>
              <Select
                value={timePreset}
                onChange={handleTimePresetChange}
              >
                <MenuItem value="all">Tất cả thời gian</MenuItem>
                <MenuItem value="today">Hôm nay</MenuItem>
                <MenuItem value="this_week">Tuần này</MenuItem>
                <MenuItem value="this_month">Tháng này</MenuItem>
                {timePreset === 'custom' && <MenuItem value="custom">Tùy chọn</MenuItem>}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <TextField
              label="Từ ngày"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true, style: { backgroundColor: 'white', padding: '0 4px' } }}
            />
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <TextField
              label="Đến ngày"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true, style: { backgroundColor: 'white', padding: '0 4px' } }}
            />
          </Grid>

          {(isAdmin || isAccountant || isLeadMarketing) && (
            <Grid item xs={12} sm={3} md={3}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px' }}>
                  Nhân viên Sale
                </InputLabel>
                <Select
                  value={filterSale}
                  onChange={e => setFilterSale(e.target.value)}
                >
                  <MenuItem value="all">Tất cả nhân viên Sale</MenuItem>
                  {(listSales || []).map(u => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.full_name || u.fullName || u.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {(fromDate || toDate || timePreset !== 'all' || filterSale !== 'all') && (
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon style={{ fontSize: 16 }} />}
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                  setFilterSale('all');
                }}
                style={{
                  height: 40,
                  borderRadius: 8,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#D9042F',
                  borderColor: '#D9042F66',
                  backgroundColor: '#fff1f0'
                }}
              >
                Xóa bộ lọc
              </Button>
            </Grid>
          )}

          <Grid item style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {loadingAll && <CircularProgress size={16} />}
              <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                {loadingAll ? (
                  <>Đang tải: <span style={{ color: '#3b82f6', fontWeight: 700 }}>{filteredOrders.length} / {totalServerOrders}</span> đơn hàng</>
                ) : (
                  <>Tổng: <span style={{ color: '#3b82f6', fontWeight: 700 }}>{totalServerOrders}</span> đơn • <span style={{ color: '#10b981', fontWeight: 700 }}>{totalServerReceipts}</span> phiếu thu</>
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* KPI Cards Grid - Uniform Height & Alignment */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3} className={classes.kpiGridItem}>
          <Card className={classes.kpiCard}>
            <Box style={{ flexGrow: 1 }}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Tổng Tiền Các Đơn
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 800, color: '#3b82f6', marginTop: 4, marginBottom: 4 }}>
                {formatMoney(kpiData.totalRevenue)}
              </Typography>
              <Typography variant="caption" style={{ fontSize: 12, color: '#64748b' }}>
                Đã giao xong: <strong style={{ color: '#2563eb' }}>{formatMoney(kpiData.deliveredRevenue)}</strong>
              </Typography>
            </Box>
            <Box className={classes.iconBox} style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
              <AttachMoneyIcon fontSize="large" />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={classes.kpiGridItem}>
          <Card className={classes.kpiCard}>
            <Box style={{ flexGrow: 1 }}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Tiền Đã Thu (Phiếu Thu)
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 800, color: '#10b981', marginTop: 4, marginBottom: 4 }}>
                {formatMoney(kpiData.actualReceived)}
              </Typography>
              <Typography variant="caption" style={{ fontSize: 12, color: '#64748b' }}>
                Còn nợ: <strong style={{ color: '#d97706' }}>{formatMoney(kpiData.debtAmount)}</strong> ({kpiData.totalReceipts} phiếu)
              </Typography>
            </Box>
            <Box className={classes.iconBox} style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
              <CheckCircleIcon fontSize="large" />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={classes.kpiGridItem}>
          <Card className={classes.kpiCard}>
            <Box style={{ flexGrow: 1 }}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Tổng Số Đơn Hàng
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 800, color: '#f59e0b', marginTop: 4, marginBottom: 4 }}>
                {kpiData.totalOrders} đơn
              </Typography>
              <Typography variant="caption" style={{ fontSize: 12, color: '#64748b' }}>
                Xử lý: <strong>{kpiData.pendingCount}</strong> | Xong: <strong>{kpiData.completedCount}</strong>{kpiData.cancelledCount > 0 ? <> | Hủy: <strong>{kpiData.cancelledCount}</strong></> : ''}
              </Typography>
            </Box>
            <Box className={classes.iconBox} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
              <ShoppingCartIcon fontSize="large" />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={classes.kpiGridItem}>
          <Card className={classes.kpiCard}>
            <Box style={{ flexGrow: 1 }}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Số Thiết Bị Bán Ra
              </Typography>
              <Typography variant="h5" style={{ fontWeight: 800, color: '#8b5cf6', marginTop: 4, marginBottom: 4 }}>
                {kpiData.totalDevicesCount} cái
              </Typography>
              <Typography variant="caption" style={{ fontSize: 12, color: '#64748b' }}>
                Tỷ lệ hoàn thành: <strong>{kpiData.completionRate}%</strong>
              </Typography>
            </Box>
            <Box className={classes.iconBox} style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
              <DevicesIcon fontSize="large" />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Main Bar Chart with Clean 2-Bar Views */}
        <Grid item xs={12} md={8}>
          <Card className={classes.chartCard}>
            <Box className={classes.chartHeader}>
              <Typography className={classes.chartTitle}>
                {metricView === 'revenue'
                  ? '📊 Biểu Đồ Doanh Thu Theo Thời Gian'
                  : '📦 Biểu Đồ Số Lượng Đơn & Thiết Bị'}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                {/* Metric View Switcher */}
                <Box display="flex" style={{ gap: 2, backgroundColor: '#f1f5f9', padding: 3, borderRadius: 8 }}>
                  <Button
                    size="small"
                    onClick={() => setMetricView('revenue')}
                    style={{
                      fontSize: 11,
                      height: 24,
                      padding: '2px 10px',
                      textTransform: 'none',
                      fontWeight: metricView === 'revenue' ? 700 : 500,
                      backgroundColor: metricView === 'revenue' ? '#ffffff' : 'transparent',
                      color: metricView === 'revenue' ? '#3b82f6' : '#64748b',
                      boxShadow: metricView === 'revenue' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    💰 Doanh thu
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setMetricView('volume')}
                    style={{
                      fontSize: 11,
                      height: 24,
                      padding: '2px 10px',
                      textTransform: 'none',
                      fontWeight: metricView === 'volume' ? 700 : 500,
                      backgroundColor: metricView === 'volume' ? '#ffffff' : 'transparent',
                      color: metricView === 'volume' ? '#3b82f6' : '#64748b',
                      boxShadow: metricView === 'volume' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    📦 Sản lượng (Đơn & TB)
                  </Button>
                </Box>

                {/* Time Grouping Toggle */}
                <Box display="flex" style={{ gap: 2, backgroundColor: '#f1f5f9', padding: 3, borderRadius: 8 }}>
                  <Button
                    size="small"
                    onClick={() => setGroupBy('day')}
                    style={{
                      fontSize: 11,
                      height: 24,
                      padding: '2px 8px',
                      textTransform: 'none',
                      fontWeight: effectiveGroupBy === 'day' ? 700 : 500,
                      backgroundColor: effectiveGroupBy === 'day' ? '#ffffff' : 'transparent',
                      color: effectiveGroupBy === 'day' ? '#3b82f6' : '#64748b',
                      boxShadow: effectiveGroupBy === 'day' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    Ngày
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setGroupBy('week')}
                    style={{
                      fontSize: 11,
                      height: 24,
                      padding: '2px 8px',
                      textTransform: 'none',
                      fontWeight: effectiveGroupBy === 'week' ? 700 : 500,
                      backgroundColor: effectiveGroupBy === 'week' ? '#ffffff' : 'transparent',
                      color: effectiveGroupBy === 'week' ? '#3b82f6' : '#64748b',
                      boxShadow: effectiveGroupBy === 'week' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    Tuần
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setGroupBy('month')}
                    style={{
                      fontSize: 11,
                      height: 24,
                      padding: '2px 8px',
                      textTransform: 'none',
                      fontWeight: effectiveGroupBy === 'month' ? 700 : 500,
                      backgroundColor: effectiveGroupBy === 'month' ? '#ffffff' : 'transparent',
                      color: effectiveGroupBy === 'month' ? '#3b82f6' : '#64748b',
                      boxShadow: effectiveGroupBy === 'month' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    Tháng
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box style={{ position: 'relative', flexGrow: 1, minHeight: 300 }}>
              <Bar
                key={`${metricView}-${effectiveGroupBy}`}
                data={revenueChartData}
                redraw={true}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: {
                        font: {
                          family: "'Inter', Roboto, sans-serif",
                          size: 12,
                          weight: '600'
                        },
                        color: '#64748b'
                      }
                    },
                    y: {
                      type: 'linear',
                      display: true,
                      position: 'left',
                      title: {
                        display: true,
                        text:
                          metricView === 'revenue'
                            ? 'Tiền đơn (Triệu VNĐ)'
                            : 'Số lượng (Đơn / Thiết bị)',
                        font: {
                          family: "'Inter', Roboto, sans-serif",
                          size: 12,
                          weight: '700'
                        },
                        color: '#475569'
                      },
                      grid: { color: '#f1f5f9' },
                      ticks: {
                        font: {
                          family: "'Inter', Roboto, sans-serif",
                          size: 12,
                          weight: '600'
                        },
                        color: '#64748b'
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        font: {
                          family: "'Inter', Roboto, sans-serif",
                          size: 12,
                          weight: '600'
                        },
                        color: '#1e293b',
                        padding: 16,
                        usePointStyle: true,
                        pointStyleWidth: 8
                      }
                    },
                    tooltip: {
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      titleFont: {
                        family: "'Inter', Roboto, sans-serif",
                        size: 13,
                        weight: '700'
                      },
                      bodyFont: {
                        family: "'Inter', Roboto, sans-serif",
                        size: 12,
                        weight: '500'
                      },
                      padding: 12,
                      cornerRadius: 8
                    }
                  }
                }}
              />
            </Box>
          </Card>
        </Grid>

        {/* Top Device Types Horizontal Bar */}
        <Grid item xs={12} md={4}>
          <Card className={classes.chartCard}>
            <Box className={classes.chartHeader}>
              <Typography className={classes.chartTitle}>
                🏷️ Top Loại Thiết Bị Bán Chạy
              </Typography>
            </Box>
            <Box style={{ position: 'relative', flexGrow: 1, minHeight: 300 }}>
              <Bar
                data={topDevicesChartData}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      grid: { color: '#f1f5f9' },
                      ticks: {
                        font: {
                          family: "'Inter', Roboto, sans-serif",
                          size: 12,
                          weight: '600'
                        },
                        color: '#64748b'
                      }
                    },
                    y: {
                      grid: { display: false },
                      ticks: {
                        font: {
                          family: "'Inter', Roboto, sans-serif",
                          size: 12,
                          weight: '700'
                        },
                        color: '#334155'
                      }
                    }
                  },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      titleFont: {
                        family: "'Inter', Roboto, sans-serif",
                        size: 13,
                        weight: '700'
                      },
                      bodyFont: {
                        family: "'Inter', Roboto, sans-serif",
                        size: 12,
                        weight: '500'
                      },
                      padding: 12,
                      cornerRadius: 8
                    }
                  }
                }}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 2 - Redesigned Clean Donuts & Custom Side Badges */}
      <Grid container spacing={3}>
        {/* Payment Methods Doughnut Chart */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Box className={classes.chartHeader}>
              <Typography className={classes.chartTitle}>
                💳 Phân Bổ Hình Thức Thanh Toán
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="center" style={{ flexGrow: 1 }}>
              <Grid item xs={12} sm={6}>
                <Box style={{ position: 'relative', height: 210, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Doughnut
                    data={paymentMethodDetails.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '72%',
                      plugins: {
                        legend: { display: false }
                      }
                    }}
                  />
                  <Box
                    style={{
                      position: 'absolute',
                      textAlign: 'center',
                      pointerEvents: 'none'
                    }}
                  >
                    <Typography style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
                      {paymentMethodDetails.total}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#64748b', fontSize: 11 }}>
                      Đơn hàng
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box className={classes.legendList}>
                  {paymentMethodDetails.items.map((item, idx) => (
                    <Box key={idx} className={classes.legendItem}>
                      <Box display="flex" alignItems="center">
                        <span className={classes.legendDot} style={{ backgroundColor: item.color }} />
                        <Typography style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                          {item.label}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                          {item.count} đơn
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            backgroundColor: `${item.color}15`,
                            color: item.color,
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 11
                          }}
                        >
                          {item.percent}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Customer Type Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Box className={classes.chartHeader}>
              <Typography className={classes.chartTitle}>
                👥 Phân Loại Khách Hàng (Đại Lý / Khách Lẻ)
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="center" style={{ flexGrow: 1 }}>
              <Grid item xs={12} sm={6}>
                <Box style={{ position: 'relative', height: 210, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Pie
                    data={customerTypeDetails.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false }
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box className={classes.legendList}>
                  {customerTypeDetails.items.map((item, idx) => (
                    <Box key={idx} className={classes.legendItem}>
                      <Box display="flex" alignItems="center">
                        <span className={classes.legendDot} style={{ backgroundColor: item.color }} />
                        <Typography style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                          {item.label}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                          {item.count} đơn
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            backgroundColor: `${item.color}15`,
                            color: item.color,
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 11
                          }}
                        >
                          {item.percent}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesDashboard;
